import type { FunctionComponent } from 'react';

import {
  Box,
  Button,
  Group,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { useCreateAutoCampaignPreset } from 'api/autoCampaignPresets';
import type { AutoCampaignPresetError } from 'api/autoCampaignPresets';
import { NOTIFICATION_VARIANTS } from 'constants/chownow';
import { DATE_MDY_FULL } from 'constants/dateTime';
import DockedDrawerFormContent from 'primitives/DockedDrawer/DockedDrawerFormContent';

import { useTranslation } from 'i18n';
import {
  buildCreatePresetPayload,
  DESCRIPTION_MAX_LENGTH,
  DISCOUNT_KEY_MAX_LENGTH,
  EMPTY_DRAFT,
  isDuplicateNameError,
  isSeasonal,
  NAME_MAX_LENGTH,
  TRIGGER_DESCRIPTION_MAX_LENGTH,
  validateDescription,
  validateDiscountKey,
  validateEndDate,
  validateName,
  validateStartDate,
  validateTriggerDescription,
  validateXName,
  X_NAME_MAX_LENGTH,
} from './helpers';
import type { FormValues } from './types';

const NS = 'autoCampaignSettings';

function showToast(variant: 'caution' | 'success', message: string) {
  notifications.show({
    message,
    variant: NOTIFICATION_VARIANTS[variant].label,
    icon: NOTIFICATION_VARIANTS[variant].icon,
  });
}

export type CreateCampaignFormProps = {
  onClose: () => void;
};

export const CreateCampaignForm: FunctionComponent<CreateCampaignFormProps> = ({ onClose }) => {
  const { t } = useTranslation(NS);
  const createPreset = useCreateAutoCampaignPreset();

  const form = useForm<FormValues>({
    initialValues: EMPTY_DRAFT,
    validate: {
      description: value => validateDescription(value, t),
      discountKey: value => validateDiscountKey(value, t),
      endDate: (value, values) => validateEndDate(value, values, t),
      name: value => validateName(value, t),
      startDate: (value, values) => validateStartDate(value, values, t),
      triggerDescription: value => validateTriggerDescription(value, t),
      xName: value => validateXName(value, t),
    },
  });

  const seasonal = isSeasonal(form.values.campaignType);
  const descriptionLength = form.values.description.length;

  const handleError = (error: AutoCampaignPresetError) => {
    if (error.status === 403) {
      showToast('caution', t('CreateCampaignForm.forbiddenError'));
      return;
    }

    // A duplicate name is fixed in the field it came from, so keep the drawer open.
    if (error.message && isDuplicateNameError(error.message)) {
      form.setFieldError('name', error.message);
      return;
    }

    showToast('caution', error.message || t('CreateCampaignForm.createError'));
  };

  const handleSubmit = (values: FormValues) => {
    createPreset.mutate(buildCreatePresetPayload(values), {
      onSuccess: preset => {
        showToast('success', t('CreateCampaignForm.createSuccess', { name: preset.name }));
        onClose();
      },
      onError: handleError,
    });
  };

  const dockButtons = (
    <>
      <Button variant="outline" onClick={onClose} disabled={createPreset.isPending}>
        {t('CreateCampaignForm.cancel')}
      </Button>
      <Button type="submit" loading={createPreset.isPending}>
        {t('CreateCampaignForm.create')}
      </Button>
    </>
  );

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <DockedDrawerFormContent dockButtons={dockButtons}>
        <Stack gap="sm">
          <SegmentedControl
            fullWidth
            size="xs"
            data={[
              { label: t('CreateCampaignForm.evergreen'), value: 'evergreen' },
              { label: t('CreateCampaignForm.seasonal'), value: 'seasonal' },
            ]}
            key={form.key('campaignType')}
            {...form.getInputProps('campaignType')}
          />
          <TextInput
            label={t('CreateCampaignForm.nameLabel')}
            withAsterisk
            data-autofocus
            maxLength={NAME_MAX_LENGTH}
            key={form.key('name')}
            {...form.getInputProps('name')}
          />
          <TextInput
            label={t('CreateCampaignForm.xNameLabel')}
            description={t('CreateCampaignForm.xNameHelp')}
            inputWrapperOrder={['label', 'input', 'description', 'error']}
            withAsterisk
            maxLength={X_NAME_MAX_LENGTH}
            key={form.key('xName')}
            {...form.getInputProps('xName')}
          />
          {seasonal && (
            <Group grow align="flex-start" gap="sm">
              <DatePickerInput
                label={t('CreateCampaignForm.startDateLabel')}
                placeholder={t('CreateCampaignForm.datePlaceholder')}
                valueFormat={DATE_MDY_FULL}
                withAsterisk
                key={form.key('startDate')}
                {...form.getInputProps('startDate')}
              />
              <DatePickerInput
                label={t('CreateCampaignForm.endDateLabel')}
                placeholder={t('CreateCampaignForm.datePlaceholder')}
                valueFormat={DATE_MDY_FULL}
                withAsterisk
                minDate={form.values.startDate ?? undefined}
                key={form.key('endDate')}
                {...form.getInputProps('endDate')}
              />
            </Group>
          )}
          <Box>
            <Textarea
              label={t('CreateCampaignForm.descriptionLabel')}
              withAsterisk
              autosize
              minRows={5}
              maxLength={DESCRIPTION_MAX_LENGTH}
              // Error drops out of the wrapper so it can share a line with the counter.
              inputWrapperOrder={['label', 'input']}
              key={form.key('description')}
              {...form.getInputProps('description')}
            />
            <Group justify="space-between" wrap="nowrap" gap="sm" mt="xs3">
              <Text
                variant="sublabel-xs"
                c={form.errors.description ? 'var(--content-caution)' : undefined}
              >
                {form.errors.description ?? t('CreateCampaignForm.descriptionHelp')}
              </Text>
              <Text variant="sublabel-xs">
                {descriptionLength}/{DESCRIPTION_MAX_LENGTH}
              </Text>
            </Group>
          </Box>
          <TextInput
            label={t('CreateCampaignForm.triggerDescriptionLabel')}
            withAsterisk
            maxLength={TRIGGER_DESCRIPTION_MAX_LENGTH}
            key={form.key('triggerDescription')}
            {...form.getInputProps('triggerDescription')}
          />
          <TextInput
            label={t('CreateCampaignForm.discountKeyLabel')}
            mb="lg"
            maxLength={DISCOUNT_KEY_MAX_LENGTH}
            key={form.key('discountKey')}
            {...form.getInputProps('discountKey')}
          />
        </Stack>
      </DockedDrawerFormContent>
    </form>
  );
};
